import { getSocialProfile } from "components/Beneficiar/utils";
import CountryAutocomplete from "components/CountryAutocomplete";
import { handleScrollToErrors } from "components/NewForm/utils";
import type { TAddressAutococompleteOption } from "components/shared/AddressAutocomplete";
import AddressAutocomplete, { getAddressEditOption } from "components/shared/AddressAutocomplete";
import CheckboxQuestion from "components/shared/CheckboxQuestion";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import FreeAutocomplete from "components/shared/FreeAutocomplete";
import SocialProfileAutocomplete from "components/shared/SocialProfileAutocomplete";
import StyledButton from "components/shared/StyledButton";
import TextButton from "components/shared/TextButton";
import TitleSeparation from "components/shared/TitleSeparation";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { getSocialSecurityPlaceholder, isObjectEmpty, socialSecurityFormatAcceptsLetters } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { createRepresentative, updateRepresentative } from "store/beneficiaryStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch } from "store/store";
import { BeneficiaryVAT } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

interface IRepresentativeForm {
    /**
     * Data of beneficiary linked to representative.
     */
    beneficiary: IBeneficiar | null;
    /**
     * Data of the representative for updates.
     */
    representative?: IRepresentative | undefined;
    /**
     * If form is new or edit.
     */
    formMode: string;
    /**
     * Closes modal + updates representatives state.
     */
    onClose: ((newRepresentative: IRepresentative) => Promise<void>) | (() => void);
    setModalType?: Dispatch<SetStateAction<string>>;
}

export interface IRepresentativeFormValues {
    beneficiarId: number;
    name: string;
    surname: string;
    description: { value: string; label: string };
    socialProfileType: { type: string; description: string };
    precount: { value: number; label: string };
    vat: { value: number; label: string };
    siretNumber: string;
    socialSecurityNumber: string;
    street: string | TAddressAutococompleteOption;
    zipCode: string;
    city: string;
    country: { name: string; flag: string };
    computeFromRepresentative: boolean;
}

export type TRepresentativeValueNames =
    | "beneficiarId"
    | "name"
    | "surname"
    | "description"
    | "socialProfileType"
    | "precount"
    | "vat"
    | "siretNumber"
    | "socialSecurityNumber"
    | "street"
    | "zipCode"
    | "city"
    | "country"
    | "computeFromRepresentative";

type TDefaultRepresentativeRoles = "heir" | "author" | "agent" | "editor";
const defaultRepresentativeRoles: TDefaultRepresentativeRoles[] = ["heir", "author", "agent", "editor"];

export const representativeRolesLabels = (): Record<TDefaultRepresentativeRoles, string> => {
    return {
        heir: t("Representatives.Form.Heir"),
        author: t("Representatives.Form.Author"),
        agent: t("Representatives.Form.Agent"),
        editor: t("Representatives.Form.Editor"),
    };
};

export const mandatoryURSSAFFields = {
    firstname: ["1", "2", "7", "8", "9", "L", "A", "B", "C", "H"],
    surname: ["1", "2", "7", "L", "A", "B", "C", "H"],
    NIR: ["1", "2", "5", "6", "7", "8", "9", "L", "A", "B"],
    street: ["1", "2", "6", "7", "8", "9", "L", "A", "B", "C", "H"],
    zipCode: ["1", "2", "6", "7", "8", "9", "L", "A", "B", "C", "H"],
    city: ["1", "2", "6", "7", "8", "9", "L", "A", "B", "C", "H"],
};

/**
 * Within Representatives component, form to either create or update a representative.
 */
const RepresentativeForm = ({ beneficiary, formMode, representative, onClose, setModalType }: IRepresentativeForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const selectRef = useRef(null);
    const formRef = useRef<HTMLFormElement | null>(null);
    const blockRef = useRef<HTMLDivElement | null>(null);

    const { companyInfos, getCompanySettings, countryFeatures } = useCompanySettings();
    const usesSocialProfileType = countryFeatures.precountMode === "social_profile_type";
    const roles = JSON.parse(getCompanySettings("representative_roles"));
    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");

    const [useRepresentativeData, setUseRepresentativeData] = useState(
        formMode === "edit" ? !!representative?.computeFromRepresentative : false,
    );

    const [submitPending, setSubmitPending] = useState(false);

    const vatList = countryFeatures.vat.options
        .filter((o) => o.value !== BeneficiaryVAT.TWENTY_PERCENT || usesTwentyPercentVat)
        .map((o) => ({ value: o.value, label: t(o.label) }));

    // Countries without precount options still need a default value for submission
    const precountOptions = countryFeatures.precount.options.length
        ? countryFeatures.precount.options.map((o) => ({ value: o.value, label: t(o.label) }))
        : [
              { value: 0, label: t("Beneficiars.PrecountNo") },
              { value: 1, label: t("Beneficiars.PrecountYes") },
          ];

    const roleOptions: { value: string; label: string }[] = useMemo(() => {
        return roles.map((role) => {
            if (defaultRepresentativeRoles.includes(role)) {
                return { value: role, label: representativeRolesLabels()[role] };
            } else {
                return { value: role, label: role };
            }
        });
    }, [roles]);

    const defaultValues: IRepresentativeFormValues = useMemo(() => {
        const vat = vatList.find((item) => item.value === representative?.vat);
        const precount = precountOptions.find((item) => item.value === representative?.precount);
        const role = roleOptions.find((item) => item.value === representative?.description);

        return {
            beneficiarId: beneficiary?.id ?? -1,
            name: formMode === "edit" ? representative?.name ?? "" : "",
            surname: formMode === "edit" ? representative?.surname ?? "" : "",
            description: formMode === "edit" ? role ?? roleOptions[0] : { value: "", label: "" },
            socialProfileType:
                formMode === "edit" && representative
                    ? getSocialProfile(representative, companyInfos?.distributorType) ?? {
                          type: "1",
                          description: "Artistes-Auteurs Assujettis aux précomptes",
                      }
                    : { type: "1", description: "Artistes-Auteurs Assujettis aux précomptes" },
            precount: formMode === "edit" && representative ? precount ?? precountOptions[0] : precountOptions[0],
            vat: formMode === "edit" ? vat ?? vatList[0] : vatList[0],
            siretNumber: formMode === "edit" ? representative?.siretNumber ?? "" : "",
            socialSecurityNumber: formMode === "edit" ? representative?.socialSecurityNumber ?? "" : "",
            street: formMode === "edit" ? getAddressEditOption(representative) ?? "" : "",
            zipCode: formMode === "edit" ? representative?.zipCode ?? "" : "",
            city: formMode === "edit" ? representative?.city ?? "" : "",
            country: { name: formMode === "edit" ? representative?.country ?? "" : "", flag: "" },
            /**
             * Whether the net amounts of terms must be computed based on representative social/tax profile infos or the beneficiary's.
             */
            computeFromRepresentative: formMode === "edit" ? !!representative?.computeFromRepresentative : false,
        };
    }, [representative, formMode, beneficiary]);

    const {
        control,
        formState: { errors },
        handleSubmit,
        watch,
        setValue,
    } = useForm<IRepresentativeFormValues>({ defaultValues, mode: "all" });

    /**
     * Triggers submit of form as I've placed button outside form (for design purposes).
     */
    const handleTriggerSubmit = () => {
        if (!isObjectEmpty(errors)) {
            handleScrollToErrors(errors);
        }
        if (formRef.current) {
            const submitEvent = new Event("submit", { cancelable: true, bubbles: true });
            formRef.current.dispatchEvent(submitEvent);

            if (isObjectEmpty(errors)) {
                const top = formRef.current.scrollTop;
                formRef.current.scrollTo(top, 0);
            }
        }
    };

    const onSubmit = async (data: IRepresentativeFormValues) => {
        setSubmitPending(true);

        try {
            // Because the field of street is an autocomplete,
            // if they selected an option, the value is an object.
            if (typeof data.street !== "string") {
                data.street = (data.street as TAddressAutococompleteOption).street;
            }

            if (beneficiary) {
                const newRepresentative = {
                    ...data,
                    computeFromRepresentative: useRepresentativeData,
                    socialProfileType: useRepresentativeData
                        ? data.socialProfileType?.type
                        : getSocialProfile(beneficiary, companyInfos?.distributorType)?.type ?? "1",
                    vat: useRepresentativeData ? data.vat.value : beneficiary.vat,
                    precount: useRepresentativeData ? data.precount.value : beneficiary.precount,
                    siretNumber: useRepresentativeData ? data.siretNumber : beneficiary.siretNumber,
                    socialSecurityNumber: useRepresentativeData
                        ? data.socialSecurityNumber
                        : beneficiary.socialSecurityNumber,
                    country: data.country?.name,
                    description: (data.description as any)?.value ?? "",
                    street: data.street as string,
                };

                if (formMode === "new") {
                    const response = await dispatch(
                        createRepresentative({ beneficiaryId: beneficiary.id, data: newRepresentative }),
                    );
                    if (response.meta.requestStatus === "fulfilled") {
                        onClose(response.payload);

                        // If description isn't string, it means they've selected a role (not default value).
                        // We then check if the role already exists in database.
                        if (
                            typeof data.description !== "string" &&
                            data.description.value !== "" &&
                            !roles.includes(data.description.value)
                        ) {
                            const newRoles = [...roles, data.description.value];
                            dispatch(
                                putCompanySettings([
                                    {
                                        settingsName: "representative_roles",
                                        settingsValue: JSON.stringify(newRoles),
                                    },
                                ]),
                            );
                        }
                    }
                } else if (representative) {
                    const response = await dispatch(
                        updateRepresentative({
                            beneficiaryId: beneficiary.id,
                            representativeId: representative?.id,
                            data: newRepresentative,
                        }),
                    );
                    if (response.meta.requestStatus === "fulfilled") {
                        await onClose(response.payload);
                    }
                }
            }
        } finally {
            setSubmitPending(false);
        }
    };

    const chosenDescription = watch("description");
    const chosenSocialType = watch("socialProfileType").type;

    return (
        <>
            <form className={styles["representative-form"]} onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <div className={styles["double-field"]}>
                    <TextFormComponent
                        control={control}
                        controlName="name"
                        label={t("Representatives.Form.Name")}
                        error={errors.name}
                    />
                    <TextFormComponent
                        control={control}
                        controlName="surname"
                        label={t("Representatives.Form.Surname")}
                        required
                        error={errors.surname}
                    />
                </div>
                <FreeAutocomplete
                    label={t("Representatives.Form.Description")}
                    basicOptions={roleOptions}
                    control={control}
                    controlName={"description"}
                    errors={errors}
                    placeholder={t("Representatives.Form.RolePlaceholder")}
                    chosenOption={chosenDescription}
                    setChosenOption={(newValue) => setValue("description", newValue)}
                />

                <CheckboxQuestion
                    checked={useRepresentativeData}
                    onChange={() => setUseRepresentativeData((prev) => !prev)}
                    text={t("Representatives.Form.CheckboxLabel")}
                    textStyle={{ color: variables.foreground } as React.CSSProperties}
                    infoTooltip={{ text: t("Representatives.Form.CheckboxTooltip"), type: "info" }}
                />

                <CSSTransition
                    in={useRepresentativeData}
                    nodeRef={blockRef}
                    timeout={300}
                    classNames={"representative-conditional-block"}
                    unmountOnExit
                >
                    <div ref={blockRef} className={styles["representative-conditional-block"]}>
                        <div className={styles["representative-conditional-inner"]}>
                            <TitleSeparation
                                title={t("Representatives.Form.Address")}
                                titleStyle={{ fontSize: "14px" } as React.CSSProperties}
                            />
                            <AddressAutocomplete
                                control={control}
                                setValue={setValue}
                                streetValue={watch("street")}
                                error={errors.street}
                                required={
                                    usesSocialProfileType && mandatoryURSSAFFields.street.includes[chosenSocialType]
                                }
                            />
                            <div className={styles["double-field"]}>
                                <TextFormComponent
                                    control={control}
                                    controlName="zipCode"
                                    label={t("Representatives.Form.ZipCode")}
                                    error={errors.zipCode}
                                    placeholder={t("AddressPlaceholders.ZipCode")}
                                    required={
                                        usesSocialProfileType &&
                                        mandatoryURSSAFFields.zipCode.includes[chosenSocialType]
                                    }
                                />
                                <TextFormComponent
                                    control={control}
                                    controlName="city"
                                    label={t("Representatives.Form.City")}
                                    placeholder={t("AddressPlaceholders.City")}
                                    error={errors.city}
                                    customValidate={(value) => value === "" || !/\d/?.test(value)}
                                    customValidateMessage={t("NewElement.OnlyText")}
                                    required={
                                        usesSocialProfileType && mandatoryURSSAFFields.city.includes[chosenSocialType]
                                    }
                                />
                            </div>
                            <CountryAutocomplete control={control} errors={errors} />
                            <TitleSeparation
                                title={t("Representatives.Form.ProfileTitle")}
                                titleStyle={{ fontSize: "14px" } as React.CSSProperties}
                            />
                            {countryFeatures.precountMode === "direct" &&
                            countryFeatures.precount.options.length > 0 ? (
                                <AutocompleteFormComponent
                                    label={t("Representatives.Form.PrecountLabel")}
                                    control={control}
                                    controlName="precount"
                                    options={precountOptions}
                                    ref={selectRef}
                                    required
                                    error={errors.precount}
                                    renderOptionText={(option) => `${option.label}`}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            ) : countryFeatures.precountMode === "social_profile_type" ? (
                                <SocialProfileAutocomplete
                                    control={control}
                                    errors={errors.socialProfileType}
                                    controlName="socialProfileType"
                                    companyDistributorType={companyInfos?.distributorType}
                                    ref={selectRef}
                                    required
                                />
                            ) : null}
                            <AutocompleteFormComponent
                                control={control}
                                controlName="vat"
                                options={vatList}
                                required
                                error={errors.vat}
                                label={t("Representatives.Form.FiscalProfile")}
                                renderOptionText={(option) => `${option.label ?? ""}`}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                            />
                            {socialSecurityFormatAcceptsLetters(countryFeatures.validation.socialSecurityFormat) ? (
                                <TextFormComponent
                                    control={control}
                                    controlName="socialSecurityNumber"
                                    label={t("Beneficiars.SecurityNumber")}
                                    placeholder={getSocialSecurityPlaceholder(
                                        countryFeatures.validation.socialSecurityFormat,
                                    )}
                                    error={errors.socialSecurityNumber}
                                    required={
                                        usesSocialProfileType && mandatoryURSSAFFields.NIR.includes(chosenSocialType)
                                    }
                                />
                            ) : (
                                <NumberFormComponent
                                    control={control}
                                    controlName="socialSecurityNumber"
                                    label={t("Beneficiars.SecurityNumber")}
                                    error={errors.socialSecurityNumber}
                                    required={
                                        usesSocialProfileType && mandatoryURSSAFFields.NIR.includes(chosenSocialType)
                                    }
                                />
                            )}
                            <TextFormComponent
                                control={control}
                                controlName="siretNumber"
                                label={t("Beneficiars.Siret")}
                                error={errors.siretNumber}
                            />
                        </div>
                    </div>
                </CSSTransition>
            </form>
            <div className={styles["action-btns"]}>
                <StyledButton
                    buttonType="primary"
                    className={styles["save-button"]}
                    type="button"
                    onClick={() => handleTriggerSubmit()}
                    loading={submitPending}
                >
                    {formMode === "new" ? t("Representatives.Form.CreateButton") : t("Representatives.Form.EditButton")}
                </StyledButton>
                {formMode === "edit" && (
                    <TextButton
                        text={t("Representatives.Form.Delete")}
                        handleClick={() => setModalType && setModalType("delete")}
                        style={{ fontSize: "0.8rem", color: variables["danger-text"] }}
                        underlineOnHover
                    />
                )}
            </div>
        </>
    );
};

export default RepresentativeForm;
